//SalesSearch.tsx ==============================================================
import "../Util/Prototypes";

import * as Criteria from "./Criteria";
import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as DropdownCriteria from "./DropdownCriteria";
import * as GeoFence from "./GeoFence";
import * as GetSales from "./GetSales";
import * as GraLay from "../Util/GraLay";
import * as HeatMap from "./HeatMap";
import * as MainMap from "../MainMap";
import * as SalesSearchSettings from "./SalesSearchSettings";
import * as Search from "../Search/Search";
import * as Settings from "../Settings";
import * as TimePeriod from "./TimePeriod";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as Util_MUI from "../Util/Util_MUI";
import * as WidgetHeading from "../Widgets/WidgetHeading";

import { Button, Icon, Paper, TextField, Typography } from "@mui/material";
import { blueGrey } from '@mui/material/colors';

//ESRI javascript API.
import Graphic from "@arcgis/core/Graphic";

import React from "react";

const toolName: string = "Sales Search";

export let layer = {} as Search.iLayer; //Sales features.
layer.totRec = 0;

export let activePIN: string = "";
export let propertyType: string;
export let improvementType: string;

let ss: SalesSearchSettings.iSS; //Settings.
let prevActivePIN: string = "";

//Auto search
export function autoSearch() {
   if (ss.autoSearch) {
      GetSales.search();
   }
}

//Clear this tool.
export function clearTool() {
   GeoFence.clearTool();
}

//Clear all results.
export function clearAllResults() {
   layer.totRec = 0;
   layer.fs = null;
   GraLay.appPnt.removeAll();
   GraLay.appPoly.removeAll();
   GraLay.appPntHighlight.removeAll();
   HeatMap.clearHeatMap();
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   Util.logMsg("Top of SalesSearch.Render");

   //Initialize only one time.
   if (typeof ss === "undefined") {
      ss = SalesSearchSettings.ss; //Get settings.

      //Initialize dropdown menus.
      propertyType = ss.propertyTypes[1];
      improvementType = ss.improvementTypes[1];
   }

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      //Util.logMsg("SalesSearch did mount");
      if (DrawerMenu.curToolName === toolName) {
         loadCriteriaFromSubject();
      }
   }, []);

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Load criteria text boxes using subject parcel.
   function loadCriteriaFromSubject() {

      //Set local active pin.
      if (!Search.activePIN) {
         activePIN = "";
      }

      //Process active pin.
      if (Search.activePIN && (Search.activePIN !== prevActivePIN) && (Search.activeLayer === Settings.parcelLayerName)) {
         activePIN = Search.activePIN;

         const lay: Search.iLayer = Search.layers[Search.activeLayerIndex];
         const fs = lay.fs;
         const feature = fs.features[0];

         GeoFence.clearGeoFences();
         ss.subjectCentroid = feature.geometry.extent.center; //Get subject parcel centroid.

         //Property type.
         let propType = Util_App.getFieldValueNoCase(ss.subjectField_PropType, feature, fs) as string;

         //Custom!
         if (propType.StartsWith("Utility")) {
            propType = "Public Utility";

         } else if (propType.StartsWith("Preferential")) {
            propType = "Preferential Covenant";

         } else if (propType.StartsWith("Conservation")) {
            propType = "Conservation Covenant";

         } else {
            const sa: string[] = propType.split(" ");
            propType = sa[0];
         }

         if (ss.propertyTypes.Contains(propType)) {
            propertyType = propType;
         } else {
            Util.errMsg('"' + propType + '" is an unsupported property type.  Sales Search only works with property types: ' + ss.propertyTypes.join(","));
         }

         //Improvement type. Custom!
         const improveValue = Util_App.getFieldValueNoCase(ss.subjectField_ImproveType, feature, fs) as string;
         const improveValueInt = Util.parseInteger(improveValue);

         improvementType = "Land With Building";
         if (improveValue.toLowerCase() === "yes") { //aka: vacant is Yes.
            improvementType = "Land only";
         }
         Util.logMsg("improvementType: " + improvementType);

         //Validate field names in the settings.
         const propTypeSpec: SalesSearchSettings.iPropType = SalesSearchSettings.getPropTypeSpec(propertyType);
         const omitImprove: SalesSearchSettings.iOmitImproveTypeCriteria = SalesSearchSettings.getOmitImproveTypeSpec(improvementType);

         for (const criteriaKey of propTypeSpec.criteriaKeys) {
            if (omitImprove && omitImprove.criteriaKeys.Contains(criteriaKey)) {
               continue;
            }
            const criteria: SalesSearchSettings.iCriteria = ss[criteriaKey];
            if (criteria && (typeof criteria.weighting !== "undefined")) {
               if (Util_App.getFieldIndexByName(criteria.subjectField, fs) === -1) {
                  Util.errMsg("Field name not found in subject parcel: " + criteria.subjectField);
               }
            }
         }

         //Load subject, min, and max values.
         for (const criteriaKey of propTypeSpec.criteriaKeys) {
            if (omitImprove && omitImprove.criteriaKeys.Contains(criteriaKey)) {
               continue;
            }
            const criteria: SalesSearchSettings.iCriteria = ss[criteriaKey];
            if (criteria && (typeof criteria.weighting !== "undefined")) {

               //Subject value.
               let fieldValue = Util_App.getFieldValueNoCase(criteria.subjectField, feature, fs) as string;
               if (criteria.convertSubjectToAcres) {
                  fieldValue = Util.formatTextValue(fieldValue, "Acres");
               }
               criteria.subjectValue = Util.parseFlt(fieldValue);

               //Min and max values.
               criteria.minValue = 0;
               criteria.maxValue = 0;

               if (criteria.rangePercent > 0) {
                  const delta: number = criteria.subjectValue * (criteria.rangePercent / 100);
                  const min: number = Math.round(Util.parseFlt((criteria.subjectValue - delta).toString()));
                  let max: number = Math.round(Util.parseFlt((criteria.subjectValue + delta).toString()));

                  if (criteria.alias.toLowerCase().Contains("year built")) {
                     const today = new Date();
                     if (max > today.getFullYear()) {
                        max = 0;
                     }
                  }

                  if (min > 0) {
                     criteria.minValue = min;
                  }
                  if (max > 0) {
                     criteria.maxValue = max;
                  }
               }

            }
         }

         //Get neighborhood and optionally search for sales.
         if (GeoFence.subjectGeofenceMode === "Circle") {
            GeoFence.createCircle(ss.subjectCentroid, GeoFence.prevSearchRadius);
            MainMap.activeView.goTo(GeoFence.geoFenceGeom.extent, { duration: 0 });

         } else if (Settings.security.accessLevel >= ss.accessLevel_neighborhood) {
            GeoFence.getNeighborhoodGeom(ss.subjectCentroid);
         }

         //Restore geofence.
      } else if (GeoFence.geoFenceGeom) {
         const gra = new Graphic({ geometry: GeoFence.geoFenceGeom, symbol: ss.geomSym });
         GraLay.appPoly.add(gra);
         MainMap.activeView.goTo(GeoFence.geoFenceGeom.extent, { duration: 0 });
      } //end if active pin exists.

      prevActivePIN = Search.activePIN;
      refresh();
   }

   //Property type changed.
   function propertyTypeChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      propertyType = event.target.value;
      autoSearch();
      refresh();
   }

   //Improvement type changed.
   function improvementTypeChanged(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      improvementType = event.target.value;
      autoSearch();
      refresh();
   }

   //Clear all filters.
   function clearFilters() {
      activePIN = "";
      //propertyType = "Any";
      //improvementType = "Any";
      GeoFence.clearGeoFences();
      clearAllCriteria();
      DropdownCriteria.clearDropdownCriteria();
      TimePeriod.clearTimePeriod();
      //clearAllResults();
      refresh();
   }

   //Get criteria rows.
   function clearAllCriteria() {
      activePIN = "";
      ss.subjectCentroid = null;

      const propTypeSpec: SalesSearchSettings.iPropType = SalesSearchSettings.getPropTypeSpec(propertyType);
      const omitImprove: SalesSearchSettings.iOmitImproveTypeCriteria = SalesSearchSettings.getOmitImproveTypeSpec(improvementType);

      for (const criteriaKey of propTypeSpec.criteriaKeys) {
         if (omitImprove && omitImprove.criteriaKeys.Contains(criteriaKey)) {
            continue;
         }
         const criteria: SalesSearchSettings.iCriteria = ss[criteriaKey];

         if (criteria && (typeof criteria.weighting !== "undefined")) {
            criteria.subjectValue = 0;
            criteria.minValue = 0;
            criteria.maxValue = 0;
         }
      }
   }

   //Set render values.
   const searchIcon = <Icon color='action'>search</Icon>;
   const clearAllIcon = <Icon color='action'>clear</Icon>;

   const scrollDivID: string = toolName.replace(" ", "_") + "_Scroll";

   const styles: Default.iStyles = {
      paperYellow: { padding: 3, paddingTop: 0 },
      paperYellow2: { padding: 3, paddingTop: 3, marginTop: 7 },
      paperBeige: { padding: 3, marginTop: 7, backgroundColor: 'beige' },
      subject: { borderRadius: '0 0 2px 2px', border: '1px solid gainsboro', borderTop: '1px solid #e6e6e6', backgroundColor: blueGrey[50], paddingLeft: 9, paddingRight: 9, paddingBottom: 0 },
      pin: { color: Settings.textColorSecondary },
      clearButton: { border: '1px solid silver' },
   };

   return (
      <Paper style={{ backgroundColor: "ghostWhite" }}>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div id={scrollDivID} style={Default.styles.appToolDiv}>

            {/*__________________________ Property Types ____________________________*/}
            <Paper style={styles.paperYellow}>

               {activePIN && <div className="app-flex-row-center">
                  <Typography style={styles.subject} variant="body1">Subject:&nbsp;&nbsp;<span style={styles.pin}>{activePIN}</span></Typography>
               </div>}

               <div className="app-flex-row-around" style={{ marginTop: 14, marginBottom: 4 }}>
                  <TextField select
                     label="Property Type"
                     title="Filter search results by property type"
                     inputProps={{ id: "property-type-ss" }}
                     InputLabelProps={{ htmlFor: "property-type-ss" }}
                     value={propertyType}
                     onChange={propertyTypeChanged}
                  >
                     {Util_MUI.buildSelectMenuItems(ss.propertyTypes)}
                  </TextField>

                  <TextField select style={{ minWidth: 125 }}
                     label="Improvement Type"
                     title="Filter search results by improvement type"
                     inputProps={{ id: "improvement-type-ss" }}
                     InputLabelProps={{ htmlFor: "improvement-type-ss" }}
                     value={improvementType}
                     onChange={improvementTypeChanged}
                  >
                     {Util_MUI.buildSelectMenuItems(ss.improvementTypes)}
                  </TextField>
               </div>
            </Paper>

            {/*__________________ Sales Search Components _______________*/}
            <Paper style={styles.paperBeige}>
               <GeoFence.Render />
            </Paper>

            <Paper style={styles.paperYellow2}>
               <Criteria.Render />
            </Paper>

            <Paper style={styles.paperBeige}>
               <TimePeriod.Render />
            </Paper>

            <Paper style={styles.paperYellow2}>
               <DropdownCriteria.Render />
            </Paper>

            {/*_______________________ Action buttons ________________________*/}
            <div className="app-flex-row-evenly" style={{ marginTop: 9, marginBottom: 9 }}>
               <Button startIcon={searchIcon} variant="outlined" size="medium" color="info" onClick={GetSales.search} title="Search" aria-label="Search">
                  Search
               </Button>

               <Button startIcon={clearAllIcon} variant="outlined" size="medium" color="info" onClick={clearFilters} title="Clear all search results filters" aria-label="Clear Filters">
                  Clear Filters
               </Button>
            </div>

         </div>
      </Paper>

   ); //end return.
} //end function.
